import { supabase } from './supabase';

export type ActionItemStatus = 'open' | 'in_progress' | 'closed' | 'overdue';
export type ActionItemPriority = 'low' | 'medium' | 'high' | 'critical';

export interface ActionItemRow {
  id: string;
  tenant_id: string;
  dsr_report_id: string;
  description: string;
  assigned_to_user_id: string | null;
  assigned_to_name: string | null;
  due_date: string | null;
  status: ActionItemStatus;
  priority: ActionItemPriority;
  resolution_notes: string | null;
  closed_at: string | null;
  source_section: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface ActionItemInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  description: string;
  assignedToName: string | null;
  assignedToUserId?: string | null;
  dueDate: string | null;
  status: ActionItemStatus;
  priority: ActionItemPriority;
  resolutionNotes: string | null;
  sourceSection?: string | null;
  userId: string;
}

export async function getActionItems(
  dsrReportId: string,
): Promise<ActionItemRow[]> {
  // Priority order: critical → high → medium → low.
  // Postgres orders enum values by definition order which is what we want.
  const { data, error } = await supabase
    .from('dsr_action_items')
    .select('*')
    .eq('dsr_report_id', dsrReportId)
    .eq('is_deleted', false)
    .order('priority', { ascending: false })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ActionItemRow[];
}

export async function saveActionItem(
  params: ActionItemInput,
): Promise<ActionItemRow> {
  const closedAt =
    params.status === 'closed' ? new Date().toISOString() : null;

  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_action_items')
      .update({
        description:        params.description,
        assigned_to_name:   params.assignedToName,
        assigned_to_user_id: params.assignedToUserId ?? null,
        due_date:           params.dueDate,
        status:             params.status,
        priority:           params.priority,
        resolution_notes:   params.resolutionNotes,
        source_section:     params.sourceSection ?? null,
        closed_at:          closedAt,
        updated_at:         new Date().toISOString(),
      })
      .eq('id', params.id)
      .select('*')
      .single();
    if (error) throw new Error(error.message);
    return data as ActionItemRow;
  }

  const { data, error } = await supabase
    .from('dsr_action_items')
    .insert({
      tenant_id:          params.tenantId,
      dsr_report_id:      params.dsrReportId,
      description:        params.description,
      assigned_to_name:   params.assignedToName,
      assigned_to_user_id: params.assignedToUserId ?? null,
      due_date:           params.dueDate,
      status:             params.status,
      priority:           params.priority,
      resolution_notes:   params.resolutionNotes,
      source_section:     params.sourceSection ?? null,
      closed_at:          closedAt,
      created_by:         params.userId,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as ActionItemRow;
}

export async function deleteActionItem(itemId: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_action_items')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .eq('id', itemId);
  if (error) throw new Error(error.message);
}

export async function markActionItemClosed(
  itemId: string,
  resolutionNotes: string,
): Promise<ActionItemRow> {
  const { data, error } = await supabase
    .from('dsr_action_items')
    .update({
      status: 'closed' as ActionItemStatus,
      resolution_notes: resolutionNotes,
      closed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', itemId)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as ActionItemRow;
}
